/**
 * Formats a date range with consistent formatting
 * If dates are in the same year, the year is only shown once
 * Returns dates without leading zeros (e.g., 1.5.2023 instead of 01.05.2023)
 */
export function formatDateRange(startDateStr: string, endDateStr: string): string {
  if (!startDateStr || !endDateStr) return "";

  try {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    if (isNaN(startDate.getTime())) return "";
    if (isNaN(endDate.getTime())) return formatSingleDate(startDate);

    // If dates are the same, return just one formatted date
    if (
      startDate.toISOString().split("T")[0] ===
      endDate.toISOString().split("T")[0]
    ) {
      return formatSingleDate(startDate);
    }

    const startDay = startDate.getDate();
    const startMonth = startDate.getMonth() + 1;
    const startYear = startDate.getFullYear();

    const endDay = endDate.getDate();
    const endMonth = endDate.getMonth() + 1;
    const endYear = endDate.getFullYear();

    // Format start part (without year if same year)
    const formattedEnd = `${endDay}.${endMonth}.${endYear}`;
    let formattedStart = `${startDay}.${startMonth}.${startYear}`;

    // Format end part
    if (startYear === endYear) {
      formattedStart = `${startDay}.${startMonth}.`;
    } else {
      formattedStart = `${startDay}.${startMonth}.${startYear}`;
    }

    return `${formattedStart} – ${formattedEnd}`;
  } catch {
    return "";
  }
}

/**
 * Formats a single date in DD.MM.YYYY format without leading zeros
 */
export function formatSingleDate(date: Date): string {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

/**
 * Formats a date string to DD.MM.YYYY format without leading zeros
 */
export function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  
  try {
    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) return "";
    
    return formatSingleDate(date);
  } catch {
    return "";
  }
} 